/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';
import CheckCircle from '@hyphengroup/cms-components/components/icons/CheckCircle';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import { LoanSearchParams } from '@hyphengroup/cms-components/utils/searchParams/loanSearchParams';
import { Input } from '@material-tailwind/react';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Fade } from './cms/components-user/Fade';
import { TRANSLATIONS } from './cms/translations';
import Button from '$/components/Button';
import { useLoanSearchParams } from '$/components/cms/helpers/useLoanSearchParams';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

export interface FilterInputProps {
  help?: string;
  title: string;
  defaultValue: LoanSearchParams;
  searchParam: keyof LoanSearchParams;
  onFinished?: (params: LoanSearchParams, isError?: boolean) => void;
  /**
   * The reader's accepted entry, once it differs from what the field stood for.
   * `onFinished` also runs when a blur re-commits the same figure.
   */
  onCommit?: (value: string) => void;
  max?: number;
  min: number;
  prefix?: string;
  inputClassname?: string;
}

let timer: NodeJS.Timeout | undefined;
export const FilterInput = (props: FilterInputProps) => {
  const { loanSearchParams, setLoanSearchParams } = useLoanSearchParams(
    props.defaultValue,
  );
  const context = useClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const initialValue =
    (loanSearchParams as any)?.[props.searchParam] ??
    (props.defaultValue as any)?.[props.searchParam];

  const [displayValue, setDisplayValue] = useState(
    initialValue ? formatDisplayValue(initialValue) : '',
  );
  const [rawValue, setRawValue] = useState(initialValue || '');
  const [error, setError] = useState('');
  const [showValidationSuccess, setShowValidationSuccess] = useState(false);

  useEffect(() => {
    const newValue = (loanSearchParams as any)?.[props.searchParam] ?? '';
    setRawValue(newValue);
    setDisplayValue(newValue ? formatDisplayValue(newValue) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(loanSearchParams as any)?.[props.searchParam]]);

  const validate = (value: string) => {
    return +value <= (props.max ?? Infinity) && +value >= props.min;
  };

  const onFinished = (value: string) => {
    const validationResult = validate(value);

    if (!validationResult) {
      const formatValue = (n: number) => {
        const countOfThousands = Math.floor(n / 1000);
        return countOfThousands ? `${countOfThousands}K` : n;
      };

      const min =
        context.brand === 'moneyhero'
          ? currencyFormatterNoDecimal.format(props.min)
          : `${props.prefix ?? ''}${formatValue(+props.min)}`;

      if (props.max) {
        const baseErrorMessage = props.prefix
          ? staticText.personal_loan_error
          : staticText.personal_loan_months_error;
        const max =
          context.brand === 'moneyhero'
            ? currencyFormatterNoDecimal.format(props.max)
            : `${props.prefix ?? ''}${formatValue(+props.max)}`;
        setError(
          baseErrorMessage.replace('{{min}}', min).replace('{{max}}', max),
        );
        return;
      }

      const minErrorMessage = props.prefix
        ? staticText.personal_loan_amount_min_error
        : staticText.personal_loan_month_min_error;

      setError(minErrorMessage.replace('{{min}}', min));
      return;
    }

    clearTimeout(timer);
    setShowValidationSuccess(true);
    timer = setTimeout(() => {
      setShowValidationSuccess(false);
    }, 2000);
    setError('');

    // Before the write: `setLoanSearchParams` updates this object in place.
    const changed = +value !== +(loanSearchParams[props.searchParam] ?? NaN);
    const newLoanParams = setLoanSearchParams({
      ...loanSearchParams,
      [props.searchParam]: value,
    });
    props.onFinished?.(newLoanParams, false);
    if (changed) props.onCommit?.(value);
  };

  const onDebouncedFinished = useDebouncedCallback(onFinished, 1000);
  function formatDisplayValue(value: string): string {
    const num = value.replace(/\D/g, '');
    return num ? currencyFormatterNoDecimal.format(+num) : '';
  }

  const onChange = (inputValue: string) => {
    let cleanedValue = inputValue.replace(/[^\d-]/g, '');
    if (cleanedValue.includes('-')) {
      cleanedValue = `-${cleanedValue.replace(/-/g, '')}`;
    }
    if (cleanedValue === '-' || cleanedValue === '') {
      setDisplayValue(cleanedValue);
      setRawValue(cleanedValue);
      onDebouncedFinished(cleanedValue);
      return;
    }
    setRawValue(cleanedValue);
    setDisplayValue(formatDisplayValue(cleanedValue));
    onDebouncedFinished(cleanedValue);
  };

  const onBlur = () => {
    if (rawValue === '' || rawValue === '-' || !rawValue) {
      onChange((props.min ?? 0).toString());
      return;
    }
    onChange(rawValue);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-1">
        <span className="mhg-alpha-caption text-highlights/gray-dark">
          {props.title}
        </span>
        {props.help && (
          <Button variant="text" className="h-full p-0" aria-label="Help">
            <Tooltip
              placement="top"
              onOpenEvent="hover"
              content={props.help}
              tooltipClass="rounded-s"
              infoClass="size-4 md:size-[18px] lg:size-[18px]"
              onClick={() => {}}
            />
          </Button>
        )}
      </div>
      <form>
        <div className="relative">
          <Input
            aria-label={props.title}
            max={props.max}
            min={props.min}
            error={!!error}
            value={displayValue}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onFocus={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setDisplayValue('');
            }}
            style={{ borderWidth: '1px' }}
            containerProps={{
              className: props.inputClassname,
            }}
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className={classNames(
              'font-[600] bg-white text-text/primary hover:!border-gray-600 hover:shadow-md focus:!border-gray-600 focus:shadow-md',
              {
                '!border-border/form-error outline-borders/form-error !bg-red-light':
                  error,
              },
            )}
          />
          {!error ? (
            <Fade show={showValidationSuccess}>
              <CheckCircle className="absolute right-3 top-2 text-highlights/green-dark  md:top-3" />
            </Fade>
          ) : (
            <Fade show>
              <MaterialIcon
                icon="cancel"
                className="absolute right-3 top-2 text-highlights/red-dark  md:top-3"
              />
            </Fade>
          )}
        </div>
        {error ? (
          <div className="mhg-alpha-typography-badge mt-1.5 text-highlights/red-dark">
            {error}
          </div>
        ) : null}
      </form>
    </div>
  );
};
